// Bridges between the following:
//                                      +--------+
// EquipmentService.onControlStatus <-- |        | <--> Driver states
// EquipmentService.get             --> | Bridge |
// EquipmentService.set             --> |        | <--> Driver controls
//                                      +--------+
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { AutomaticMediaRecorderStartTimeBridge } from './AutomaticMediaRecorderStartTimeBridge';
import { CalendarBridge } from './CalendarBridge';
import { CallInListBridge } from './CallInListBridge';
import { CameraMoveBridge, CameraPositionBridge, CameraPresetBridge, CameraPresetConfigBridge, CamerasBridge, CameraSelectedBridge, CameraTrackingBridge, } from './CameraBridge';
import { ContactClosureBridge } from './ContactClosureBridge';
import { ControlInfo } from './Control';
import { DetectedBridge } from './DetectedBridge';
import { DspBridge } from './DspBridge';
import { EquipmentSettingsBridge } from './EquipmentBridge';
import { assertIsQueryParam, assertIsSetParam, } from './EquipmentServiceDriver';
import { LightingLevelBridge, LightingPowerBridge, LightingPresetBridge } from './LightingBridge';
import { ShadeOperationBridge, ShadeOperationsBridge, ShadePositionBridge, ShadePresetBridge, ShadeTiltBridge, } from './ShadeBridge';
import { State, StateCollection } from './State';
import { TelephonyBridge } from './TelephonyBridge';
import { UsbHostBridge } from './UsbBridge';
import { ControllessBridge, ParameterlessBridge, ValueBridge, ValueCollectionBridge, } from './ValueBridge';
import { ContentStateBridge, VideoInputBridge, VideoInputKeyedBridge, VideoInputsBridge, VideoMatrixRoutesBridge, } from './VideoBridge';
import { VolumeBridge } from './VolumeBridge';
export class StateBridge {
    constructor(state, equipmentId, driver, transport, valueCallback, framework, logFactory) {
        this.state = state;
        this.equipmentId = equipmentId;
        this.driver = driver;
        this.transport = transport;
        this.valueCallback = valueCallback;
        this.framework = framework;
        this.bridges = {};
        this._canQueryWhenDisconnected = false;
        this.connected = new State(true);
        this.controlInfo = {};
        this.controls = {};
        this.deferredQueries = [];
        this._hasVolumeMute = false; // only used in StateBridge, not DspStateBridge
        this.offlineControlInfo = {};
        this.powerOnQueriesDone = false;
        this.queryHandlers = {};
        this.reconfigureWhenPoweredOn = false;
        this.setFilters = {};
        this.setHandlers = {};
        this.setOperations = [];
        this.log = logFactory.channel('StateBridge');
        this.onAfterConnectedChanged = this.onAfterConnectedChanged.bind(this);
        this.onAfterPowerChanged = this.onAfterPowerChanged.bind(this);
    }
    get canQueryWhenDisconnected() {
        return this._canQueryWhenDisconnected;
    }
    set canQueryWhenDisconnected(value) {
        this._canQueryWhenDisconnected = value;
    }
    get hasVolumeMute() {
        return this._hasVolumeMute;
    }
    get isConnected() {
        return this.connected.value;
    }
    set isConnected(value) {
        this.connected.update(value);
    }
    computeControlInfo(controlName, config, overrideIsStateless) {
        return __awaiter(this, void 0, void 0, function* () {
            let control = this.driver[controlName];
            if (control === undefined ||
                (config.synchronizedCalls &&
                    controlName !== 'detected' &&
                    controlName !== 'equipmentInfo' &&
                    controlName !== 'equipmentSettings' &&
                    controlName !== 'firmwareVersion' &&
                    controlName !== 'name' &&
                    controlName !== 'serialNumber' &&
                    controlName !== 'telephony')) {
                return ControlInfo.create(undefined, config, false, this.log, overrideIsStateless);
            }
            const power = this.state.power.value;
            const controlInfo = yield ControlInfo.create(control, config, power, this.log, overrideIsStateless);
            if (!controlInfo.checkedHasControl)
                this.reconfigureWhenPoweredOn = true;
            return controlInfo;
        });
    }
    configure(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        return __awaiter(this, void 0, void 0, function* () {
            this.reset();
            this._hasVolumeMute = false;
            this.powerOnQueriesDone = false;
            this.reconfigureWhenPoweredOn = false;
            // Add power first so it gets queried first. Some drivers need to know the power state
            // before evaluating other values.
            if (this.driver.power) {
                this.controlInfo.power = yield ControlInfo.create(this.driver.power, config, true, this.log);
                if (this.controlInfo.power.hasControl) {
                    const powerBridge = new ValueBridge('power', this.state.power, this.driver.power, this.controlInfo.power, this.state.power, this.transport, this.valueCallback, this.log);
                    this.bridges.power = powerBridge;
                    this.controls.power = this.driver.power;
                    // Power is a special case. We query its initial value here so we can evaluate the
                    // power off restrictions for each control.
                    yield powerBridge.getInitialValues();
                }
            }
            // Get video inputs early since several controls need them.
            this.controlInfo.videoInputs = yield this.computeControlInfo('videoInputs', config);
            const videoInputs = this.controlInfo.videoInputs.hasControl
                ? this.state.videoInputs
                : (_a = config.videoInputs) !== null && _a !== void 0 ? _a : {};
            if (this.driver.busy) {
                this.controlInfo.busy = yield this.computeControlInfo('busy', config);
                if (this.controlInfo.busy.hasControl) {
                    this.bridges.busy = new ValueBridge('busy', this.state.busy, this.driver.busy, this.controlInfo.busy, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.busy = this.driver.busy;
                }
            }
            if (this.driver.calendar) {
                this.controlInfo.calendar = yield this.computeControlInfo('calendar', config);
                if (this.controlInfo.calendar.hasControl) {
                    this.bridges.calendar = new CalendarBridge(this.state.calendar, this.driver.calendar, this.controlInfo.calendar, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.calendar = this.driver.calendar;
                }
            }
            if (this.driver.callInList) {
                this.controlInfo.callInList = yield this.computeControlInfo('callInList', config);
                if (this.controlInfo.callInList.hasControl) {
                    this.bridges.callInList = new CallInListBridge(this.state.callInList, this.driver.callInList, this.controlInfo.callInList, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.callInList = this.driver.callInList;
                }
            }
            if (this.driver.cameras) {
                this.controlInfo.cameras = yield this.computeControlInfo('cameras', config);
                if (this.controlInfo.cameras.hasControl) {
                    this.bridges.cameras = new CamerasBridge(this.state.cameras, this.state.cameraKeys, this.driver.cameras, (_b = config.cameras) !== null && _b !== void 0 ? _b : {}, this.controlInfo.cameras, this.state.power, this.framework, this.log);
                    this.controls.cameras = this.driver.cameras;
                    if (this.driver.cameraMove) {
                        this.controlInfo.cameraMove = yield this.computeControlInfo('cameraMove', config);
                        if (this.controlInfo.cameraMove.hasControl) {
                            this.bridges.cameraMove = new CameraMoveBridge(this.state.cameraKeys, this.driver.cameraMove, this.controlInfo.cameraMove, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraMove = this.driver.cameraMove;
                        }
                    }
                    if (this.driver.cameraPosition) {
                        this.controlInfo.cameraPosition = yield this.computeControlInfo('cameraPosition', config);
                        if (this.controlInfo.cameraPosition.hasControl) {
                            this.bridges.cameraPosition = new CameraPositionBridge(this.state.cameraKeys, this.driver.cameraPosition, (_c = config.cameras) !== null && _c !== void 0 ? _c : {}, this.controlInfo.cameraPosition, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraPosition = this.driver.cameraPosition;
                        }
                    }
                    if (this.driver.cameraPreset) {
                        this.controlInfo.cameraPreset = yield this.computeControlInfo('cameraPreset', config);
                        if (this.controlInfo.cameraPreset.hasControl) {
                            if ('getKey' in this.driver.cameraPreset) {
                                this.bridges.cameraPreset = new CameraPresetConfigBridge(this.state.cameraPreset, this.driver.cameraPreset, (_d = config.cameraPresets) !== null && _d !== void 0 ? _d : {}, this.controlInfo.cameraPreset, this.state.power, this.transport, this.valueCallback, this.log);
                            }
                            else {
                                this.bridges.cameraPreset = new CameraPresetBridge(config, this.state.cameraPreset, this.driver.cameraPreset, this.controlInfo.cameraPreset, this.state.power, this.transport, this.valueCallback, this.log);
                            }
                            this.controls.cameraPreset = this.driver.cameraPreset;
                        }
                    }
                    if (this.driver.cameraSelected) {
                        this.controlInfo.cameraSelected = yield this.computeControlInfo('cameraSelected', config);
                        if (this.controlInfo.cameraSelected.hasControl) {
                            this.bridges.cameraSelected = new CameraSelectedBridge(this.state.cameraSelected, this.state.cameraKeys, this.driver.cameraSelected, this.controlInfo.cameraSelected, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraSelected = this.driver.cameraSelected;
                        }
                    }
                    else {
                        // Some drivers (e.g., Visca) implement the cameras control, but don't implement
                        // the cameraSelected control because they don't have a camera switcher. For
                        // these devices, we implement a dummy cameraSelected control to receive the
                        // cameraSelected commands from EquipmentService.
                        const dummyCameraSelectedControl = {
                            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                                return this.state.cameraSelected.value;
                            }),
                            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                                return value;
                            }),
                        };
                        this.controls.cameraSelected = dummyCameraSelectedControl;
                        const dummyCameraSelectedControlInfo = yield ControlInfo.create(dummyCameraSelectedControl, config, this.state.power.value, this.log);
                        this.bridges.cameraSelected = new CameraSelectedBridge(this.state.cameraSelected, this.state.cameraKeys, dummyCameraSelectedControl, dummyCameraSelectedControlInfo, this.state.power, this.transport, this.valueCallback, this.log);
                    }
                    if (this.driver.cameraSelfView) {
                        this.controlInfo.cameraSelfView = yield this.computeControlInfo('cameraSelfView', config);
                        if (this.controlInfo.cameraSelfView.hasControl) {
                            this.bridges.cameraSelfView = new ValueBridge('cameraSelfView', this.state.cameraSelfView, this.driver.cameraSelfView, this.controlInfo.cameraSelfView, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraSelfView = this.driver.cameraSelfView;
                        }
                    }
                    if (this.driver.cameraSelfViewAuto) {
                        this.controlInfo.cameraSelfViewAuto = yield this.computeControlInfo('cameraSelfViewAuto', config);
                        if (this.controlInfo.cameraSelfViewAuto.hasControl) {
                            this.bridges.cameraSelfViewAuto = new ValueBridge('cameraSelfViewAuto', this.state.cameraSelfViewAuto, this.driver.cameraSelfViewAuto, this.controlInfo.cameraSelfViewAuto, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraSelfViewAuto = this.driver
                                .cameraSelfViewAuto;
                        }
                    }
                    if (this.driver.cameraSelfViewPosition) {
                        this.controlInfo.cameraSelfViewPosition = yield this.computeControlInfo('cameraSelfViewPosition', config);
                        if (this.controlInfo.cameraSelfViewPosition.hasControl) {
                            this.bridges.cameraSelfViewPosition = new ValueBridge('cameraSelfViewPosition', this.state.cameraSelfViewPosition, this.driver.cameraSelfViewPosition, this.controlInfo.cameraSelfViewPosition, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraSelfViewPosition = this.driver
                                .cameraSelfViewPosition;
                        }
                    }
                    if (this.driver.cameraSelfViewPositions) {
                        this.controlInfo.cameraSelfViewPositions = yield this.computeControlInfo('cameraSelfViewPositions', config);
                        this.bridges.cameraSelfViewPositions = new ParameterlessBridge('cameraSelfViewPositions', this.state.cameraSelfViewPositions, this.driver.cameraSelfViewPositions, this.controlInfo.cameraSelfViewPositions, this.state.power, this.transport, this.log);
                    }
                    if (this.driver.cameraSelfViewAvailable) {
                        this.controlInfo.cameraSelfViewAvailable = yield this.computeControlInfo('cameraSelfViewAvailable', config);
                        if (this.controlInfo.cameraSelfViewAvailable.hasControl) {
                            this.bridges.cameraSelfViewAvailable = new ValueBridge('cameraSelfViewAvailable', this.state.cameraSelfViewAvailable, this.driver.cameraSelfViewAvailable, this.controlInfo.cameraSelfViewAvailable, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraSelfViewAvailable = this.driver
                                .cameraSelfViewAvailable;
                        }
                    }
                    else if (((_e = this.controlInfo.cameraSelfView) === null || _e === void 0 ? void 0 : _e.hasControl) ||
                        ((_f = this.controlInfo.cameraPosition) === null || _f === void 0 ? void 0 : _f.hasControl)) {
                        this.bridges.cameraSelfViewAvailable = new ControllessBridge('cameraSelfViewAvailable', this.state.cameraSelfViewAvailable, this.valueCallback, this.log);
                    }
                    if (this.driver.cameraTracking) {
                        this.controlInfo.cameraTracking = yield this.computeControlInfo('cameraTracking', config);
                        if (this.controlInfo.cameraTracking.hasControl) {
                            this.bridges.cameraTracking = new CameraTrackingBridge(this.state.cameraTracking, this.state.cameraKeys, this.driver.cameraTracking, this.controlInfo.cameraTracking, this.state.power, this.transport, this.valueCallback, this.log);
                            this.controls.cameraTracking = this.driver.cameraTracking;
                        }
                    }
                }
            }
            if (this.driver.channel) {
                this.controlInfo.channel = yield this.computeControlInfo('channel', config);
                if (this.controlInfo.channel.hasControl) {
                    this.bridges.channel = new ValueBridge('channel', this.state.channel, this.driver.channel, this.controlInfo.channel, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.channel = this.driver.channel;
                }
            }
            if (this.driver.channelKeyboardType) {
                this.controlInfo.channelKeyboardType = yield this.computeControlInfo('channelKeyboardType', config);
                if (this.controlInfo.channelKeyboardType.hasControl) {
                    this.bridges.channelKeyboardType = new ParameterlessBridge('channelKeyboardType', this.state.channelKeyboardType, this.driver.channelKeyboardType, this.controlInfo.channelKeyboardType, this.state.power, this.transport, this.log);
                    this.controls.channelKeyboardType = this.driver.channelKeyboardType;
                }
            }
            if (this.driver.channelTuner) {
                this.controlInfo.channelTuner = yield this.computeControlInfo('channelTuner', config);
                if (this.controlInfo.channelTuner.hasControl) {
                    this.bridges.channelTuner = new ValueBridge('channelTuner', this.state.channelTuner, this.driver.channelTuner, this.controlInfo.channelTuner, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.channelTuner = this.driver.channelTuner;
                }
            }
            if (this.driver.channelTuners) {
                this.controlInfo.channelTuners = yield this.computeControlInfo('channelTuners', config);
                if (this.controlInfo.channelTuners.hasControl) {
                    this.bridges.channelTuners = new ParameterlessBridge('channelTuners', this.state.channelTuners, this.driver.channelTuners, this.controlInfo.channelTuners, this.state.power, this.transport, this.log);
                    this.controls.channelTuners = this.driver.channelTuners;
                }
            }
            if (this.driver.closedCaptioning) {
                this.controlInfo.closedCaptioning = yield this.computeControlInfo('closedCaptioning', config);
                if (this.controlInfo.closedCaptioning.hasControl) {
                    this.bridges.closedCaptioning = new ValueBridge('closedCaptioning', this.state.closedCaptioning, this.driver.closedCaptioning, this.controlInfo.closedCaptioning, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.closedCaptioning = this.driver.closedCaptioning;
                }
            }
            if (this.driver.contactClosure) {
                this.controlInfo.contactClosure = yield this.computeControlInfo('contactClosure', config);
                if (this.controlInfo.contactClosure.hasControl) {
                    this.bridges.contactClosure = new ContactClosureBridge(this.state.contactClosures, this.driver.contactClosure, (_g = config.contactClosures) !== null && _g !== void 0 ? _g : {}, this.controlInfo.contactClosure, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.contactClosure = this.driver.contactClosure;
                }
            }
            if (this.driver.contentSource) {
                this.controlInfo.contentSource = yield this.computeControlInfo('contentSource', config);
                if (this.controlInfo.contentSource.hasControl) {
                    this.bridges.contentSource = new VideoInputBridge('contentSource', this.state.contentSource, this.driver.contentSource, videoInputs, this.controlInfo.contentSource, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.contentSource = this.driver.contentSource;
                }
            }
            if (this.driver.contentState) {
                this.controlInfo.contentState = yield this.computeControlInfo('contentState', config);
                if (this.controlInfo.contentState.hasControl) {
                    this.bridges.contentState = new ContentStateBridge('contentState', this.state.contentState, this.driver.contentState, videoInputs, this.controlInfo.contentState, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.contentState = this.driver.contentState;
                }
            }
            if (this.driver.contentStop) {
                this.controlInfo.contentStop = yield this.computeControlInfo('contentStop', config);
                if (this.controlInfo.contentStop.hasControl) {
                    this.bridges.contentStop = new VideoInputBridge('stopContent', new State(''), // dummy
                    this.driver.contentStop, videoInputs, this.controlInfo.contentStop, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.contentStop = this.driver.contentStop;
                }
            }
            if (this.driver.detected) {
                this.controlInfo.detected = yield this.computeControlInfo('detected', config);
                if (this.controlInfo.detected.hasControl) {
                    this.bridges.detected = new DetectedBridge(this.state.detected, this.driver.detected, this.driver.constructor.channelsConfigName, this.controlInfo.detected, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.detected = this.driver.detected;
                }
            }
            if (this.driver.directory) {
                this.controlInfo.directory = yield this.computeControlInfo('directory', config);
                if (this.controlInfo.directory.hasControl) {
                    this.bridges.directory = new ValueCollectionBridge('directory', 'directoryEntry', this.state.directory, this.driver.directory, this.controlInfo.directory, this.state.power, this.transport, this.valueCallback, this.log, this.equipmentId);
                    this.controls.directory = this.driver.directory;
                }
            }
            if (this.driver.displayBlank) {
                this.controlInfo.displayBlank = yield this.computeControlInfo('displayBlank', config);
                if (this.controlInfo.displayBlank.hasControl) {
                    this.bridges.displayBlank = new ValueBridge('displayBlank', this.state.displayBlank, this.driver.displayBlank, this.controlInfo.displayBlank, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.displayBlank = this.driver.displayBlank;
                }
            }
            if (this.driver.displayFreeze) {
                this.controlInfo.displayFreeze = yield this.computeControlInfo('displayFreeze', config);
                if (this.controlInfo.displayFreeze.hasControl) {
                    this.bridges.displayFreeze = new ValueBridge('displayFreeze', this.state.displayFreeze, this.driver.displayFreeze, this.controlInfo.displayFreeze, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.displayFreeze = this.driver.displayFreeze;
                }
            }
            if (this.driver.equipmentInfo) {
                this.controlInfo.equipmentInfo = yield this.computeControlInfo('equipmentInfo', config);
                if (this.controlInfo.equipmentInfo.hasControl) {
                    this.bridges.equipmentInfo = new ValueBridge('equipmentInfo', this.state.equipmentInfo, this.driver.equipmentInfo, this.controlInfo.equipmentInfo, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.equipmentInfo = this.driver.equipmentInfo;
                }
            }
            if (this.driver.equipmentSettings) {
                this.controlInfo.equipmentSettings = yield this.computeControlInfo('equipmentSettings', config);
                if (this.controlInfo.equipmentSettings.hasControl) {
                    this.bridges.equipmentSettings = new EquipmentSettingsBridge(this.driver.equipmentSettings, this.controlInfo.equipmentSettings, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.equipmentSettings = this.driver.equipmentSettings;
                }
            }
            if (this.driver.firmwareVersion) {
                this.controlInfo.firmwareVersion = yield this.computeControlInfo('firmwareVersion', config);
                if (this.controlInfo.firmwareVersion.hasControl) {
                    this.bridges.firmwareVersion = new ValueBridge('firmwareVersion', this.state.firmwareVersion, this.driver.firmwareVersion, this.controlInfo.firmwareVersion, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.firmwareVersion = this.driver.firmwareVersion;
                }
            }
            // lightingLevel must come before lightingPower for drivers that use the lighting level to
            // infer power.
            if (this.driver.lightingLevel) {
                this.controlInfo.lightingLevel = yield this.computeControlInfo('lightingLevel', config);
                if (this.controlInfo.lightingLevel.hasControl) {
                    this.bridges.lightingLevel = new LightingLevelBridge(this.state.lightingLevel, this.state.lightingLevelRange, this.driver.lightingLevel, (_h = config.lightingControls) !== null && _h !== void 0 ? _h : {}, this.controlInfo.lightingLevel, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.lightingLevel = this.driver.lightingLevel;
                }
            }
            if (this.driver.lightingPower) {
                this.controlInfo.lightingPower = yield this.computeControlInfo('lightingPower', config);
                if (this.controlInfo.lightingPower.hasControl) {
                    this.bridges.lightingPower = new LightingPowerBridge(this.state.lightingPower, this.driver.lightingPower, (_j = config.lightingControls) !== null && _j !== void 0 ? _j : {}, this.controlInfo.lightingPower, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.lightingPower = this.driver.lightingPower;
                }
            }
            if (this.driver.lightingPreset) {
                this.controlInfo.lightingPreset = yield this.computeControlInfo('lightingPreset', config);
                if (this.controlInfo.lightingPreset.hasControl) {
                    this.bridges.lightingPreset = new LightingPresetBridge(this.state.lightingPreset, this.driver.lightingPreset, (_k = config.lightingPresets) !== null && _k !== void 0 ? _k : {}, this.controlInfo.lightingPreset, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.lightingPreset = this.driver.lightingPreset;
                }
            }
            if (this.driver.mediaOperation) {
                this.controlInfo.mediaOperation = yield this.computeControlInfo('mediaOperation', config);
                if (this.controlInfo.mediaOperation.hasControl) {
                    this.bridges.mediaOperation = new VideoInputKeyedBridge('mediaOperation', config, this.driver.mediaOperation, new StateCollection(), // dummy
                    videoInputs, this.controlInfo.mediaOperation, this.state.power, this.transport, this.valueCallback, this.framework, this.log, mediaRequest => mediaRequest.operation);
                    this.bridges
                        .mediaOperation.removeSetCommands = false;
                    this.controls.mediaOperation = this.driver.mediaOperation;
                }
            }
            if (this.driver.mediaOperations) {
                this.controlInfo.mediaOperations = yield this.computeControlInfo('mediaOperations', config);
                if (this.controlInfo.mediaOperations.hasControl) {
                    this.bridges.mediaOperations = new ParameterlessBridge('mediaOperations', this.state.mediaOperations, this.driver.mediaOperations, this.controlInfo.mediaOperations, this.state.power, this.transport, this.log);
                    this.controls.mediaOperations = this.driver.mediaOperations;
                }
            }
            if (this.driver.mediaPlayerState) {
                this.controlInfo.mediaPlayerState = yield this.computeControlInfo('mediaPlayerState', config);
                if (this.controlInfo.mediaPlayerState.hasControl) {
                    this.bridges.mediaPlayerState = new VideoInputKeyedBridge('mediaPlayerState', config, this.driver.mediaPlayerState, this.state.mediaPlayerState, videoInputs, this.controlInfo.mediaPlayerState, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.mediaPlayerState = this.driver.mediaPlayerState;
                }
            }
            if (this.driver.mediaRecorderStartTime) {
                this.controlInfo.mediaRecorderStartTime = yield this.computeControlInfo('mediaRecorderStartTime', config);
                if (this.controlInfo.mediaRecorderStartTime.hasControl) {
                    this.bridges.mediaRecorderStartTime = new VideoInputKeyedBridge('mediaRecorderStartTime', config, this.driver.mediaRecorderStartTime, this.state.mediaRecorderStartTime, videoInputs, this.controlInfo.mediaRecorderStartTime, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.mediaRecorderStartTime = this.driver.mediaRecorderStartTime;
                }
            }
            if (this.driver.mediaRecorderState) {
                this.controlInfo.mediaRecorderState = yield this.computeControlInfo('mediaRecorderState', config);
                if (this.controlInfo.mediaRecorderState.hasControl) {
                    const mediaRecorderStateBridge = new VideoInputKeyedBridge('mediaRecorderState', config, this.driver.mediaRecorderState, this.state.mediaRecorderState, videoInputs, this.controlInfo.mediaRecorderState, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.bridges.mediaRecorderState = mediaRecorderStateBridge;
                    this.controls.mediaRecorderState = this.driver.mediaRecorderState;
                    // If the driver doesn't implement mediaRecorderStartTime, then add an automatic
                    // implementation.
                    if (!this.driver.mediaRecorderStartTime) {
                        this.controlInfo.mediaRecorderStartTime = ControlInfo.createCustom();
                        this.bridges.mediaRecorderStartTime = new AutomaticMediaRecorderStartTimeBridge(this.state, this.valueCallback, () => mediaRecorderStateBridge.getKeymap(), this.log);
                        // nothing to add to this.controls
                    }
                }
            }
            if (this.driver.mediaStreamerState) {
                this.controlInfo.mediaStreamerState = yield this.computeControlInfo('mediaStreamerState', config);
                if (this.controlInfo.mediaStreamerState.hasControl) {
                    this.bridges.mediaStreamerState = new VideoInputKeyedBridge('mediaStreamerState', config, this.driver.mediaStreamerState, this.state.mediaStreamerState, videoInputs, this.controlInfo.mediaStreamerState, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.mediaStreamerState = this.driver.mediaStreamerState;
                }
            }
            this.controlInfo.mute = yield this.computeControlInfo('mute', config);
            this.controlInfo.volume = yield this.computeControlInfo('volume', config);
            this.controlInfo.volumeMute = yield this.computeControlInfo('volumeMute', config);
            if (this.controlInfo.mute.hasControl &&
                'getKey' in ((_l = this.driver.mute) !== null && _l !== void 0 ? _l : {}) &&
                this.controlInfo.volume.hasControl &&
                'getKey' in ((_m = this.driver.volume) !== null && _m !== void 0 ? _m : {})) {
                const dspBridge = new DspBridge(this.driver, config, this.controlInfo, this.state.mutes, this.state.volumes, this.state.volumesRanges, this.state.power, this.transport, this.valueCallback, this.log);
                const result = dspBridge.setupBridges();
                this.bridges = Object.assign(Object.assign({}, this.bridges), result.bridges);
                this.controls = Object.assign(Object.assign({}, this.controls), result.controls);
            }
            else {
                if (this.controlInfo.mute.hasControl &&
                    this.driver.mute &&
                    !('getKey' in this.driver.mute)) {
                    this.bridges.mute = new ValueBridge('mute', this.state.mute, this.driver.mute, this.controlInfo.mute, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.mute = this.driver.mute;
                }
                if (this.controlInfo.volume.hasControl &&
                    this.driver.volume &&
                    !('getKey' in this.driver.volume)) {
                    if ('range' in this.driver.volume) {
                        this.bridges.volume = new VolumeBridge(config, this.state.volume, this.state.volumeRange, this.state.volumeMute, this.driver.volume, this.driver.volumeMute, this.controlInfo.volume, this.state.power, this.transport, this.valueCallback, this.log);
                    }
                    else {
                        this.bridges.volume = new ValueBridge('volume', this.state.volume, this.driver.volume, this.controlInfo.volume, this.state.power, this.transport, this.valueCallback, this.log);
                    }
                    this.controls.volume = this.driver.volume;
                }
                if (this.controlInfo.volume.hasControl &&
                    this.driver.volumeMute &&
                    'set' in this.driver.volumeMute &&
                    this.controlInfo.volumeMute.hasControl) {
                    this.bridges.volumeMute = new ValueBridge('volumeMute', this.state.volumeMute, this.driver.volumeMute, this.controlInfo.volumeMute, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.volumeMute = this.driver.volumeMute;
                    this._hasVolumeMute = true;
                }
            }
            if (this.driver.name) {
                this.controlInfo.name = yield this.computeControlInfo('name', config);
                if (this.controlInfo.name.hasControl) {
                    this.bridges.name = new ValueBridge('name', this.state.name, this.driver.name, this.controlInfo.name, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.name = this.driver.name;
                }
            }
            if (this.driver.reboot) {
                this.controlInfo.reboot = yield this.computeControlInfo('reboot', config);
                if (this.controlInfo.reboot.hasControl) {
                    this.bridges.reboot = new ValueBridge('reboot', new State(undefined), // dummy
                    this.driver.reboot, this.controlInfo.reboot, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.reboot = this.driver.reboot;
                }
            }
            if (this.driver.serialNumber) {
                this.controlInfo.serialNumber = yield this.computeControlInfo('serialNumber', config);
                if (this.controlInfo.serialNumber.hasControl) {
                    this.bridges.serialNumber = new ValueBridge('serialNumber', this.state.serialNumber, this.driver.serialNumber, this.controlInfo.serialNumber, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.serialNumber = this.driver.serialNumber;
                }
            }
            if (this.driver.shadeOperation) {
                this.controlInfo.shadeOperation = yield this.computeControlInfo('shadeOperation', config);
                if (this.controlInfo.shadeOperation.hasControl) {
                    const shadeOperationBridge = new ShadeOperationBridge(this.driver.shadeOperation, (_o = config.shadeControls) !== null && _o !== void 0 ? _o : {}, this.controlInfo.shadeOperation, this.state.power, this.transport, this.valueCallback, this.log);
                    this.bridges.shadeOperation = shadeOperationBridge;
                    this.controls.shadeOperation = this.driver.shadeOperation;
                    if (this.driver.shadeOperations) {
                        this.controlInfo.shadeOperations = yield this.computeControlInfo('shadeOperations', config);
                        if (this.controlInfo.shadeOperations.hasControl) {
                            this.bridges.shadeOperations = new ShadeOperationsBridge(this.state.shadeOperations, this.driver.shadeOperations, () => shadeOperationBridge.getKeyMap(), this.controlInfo.shadeOperations, this.state.power, this.transport, this.log);
                            this.controls.shadeOperations = this.driver.shadeOperations;
                        }
                        if (this.driver.shadePosition) {
                            this.controlInfo.shadePosition = yield this.computeControlInfo('shadePosition', config);
                            if (this.controlInfo.shadePosition.hasControl) {
                                this.bridges.shadePosition = new ShadePositionBridge(this.state.shadePosition, this.state.shadePositionRange, this.driver.shadePosition, config, () => shadeOperationBridge.getKeyMap(), this.controlInfo.shadePosition, this.state.power, this.transport, this.valueCallback, this.log);
                                this.controls.shadePosition = this.driver.shadePosition;
                            }
                        }
                        if (this.driver.shadePreset) {
                            this.controlInfo.shadePreset = yield this.computeControlInfo('shadePreset', config);
                            if (this.controlInfo.shadePreset.hasControl) {
                                this.bridges.shadePreset = new ShadePresetBridge(this.state.shadePreset, this.driver.shadePreset, (_p = config.shadePresets) !== null && _p !== void 0 ? _p : {}, this.controlInfo.shadePreset, this.state.power, this.transport, this.valueCallback, this.log);
                                this.controls.shadePreset = this.driver.shadePreset;
                            }
                        }
                        if (this.driver.shadeTilt) {
                            this.controlInfo.shadeTilt = yield this.computeControlInfo('shadeTilt', config);
                            if (this.controlInfo.shadeTilt.hasControl) {
                                this.bridges.shadeTilt = new ShadeTiltBridge(this.state.shadeTilt, this.state.shadeTiltRange, this.driver.shadeTilt, config, () => shadeOperationBridge.getKeyMap(), this.controlInfo.shadeTilt, this.state.power, this.transport, this.valueCallback, this.log);
                                this.controls.shadeTilt = this.driver.shadeTilt;
                            }
                        }
                    }
                }
            }
            this.bridges.storage = new ControllessBridge('storage', this.state.storage, this.valueCallback, this.log);
            if (this.driver.telephony) {
                this.controlInfo.telephony = yield this.computeControlInfo('telephony', config, false);
                if (this.controlInfo.telephony.hasControl) {
                    this.bridges.telephony = new TelephonyBridge(this.state.calls, this.state.callParticipants, this.state.callSupportsInvitation, this.state.maxCalls, this.state.disabledCalls, this.driver.telephony, (_q = config.telephony) !== null && _q !== void 0 ? _q : {}, this.bridges.callInList, // callInList must be initialized first
                    this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.telephony = this.driver.telephony;
                }
            }
            if (this.driver.usbHost) {
                this.controlInfo.usbHost = yield this.computeControlInfo('usbHost', config);
                if (this.controlInfo.usbHost.hasControl) {
                    this.bridges.usbHost = new UsbHostBridge('usbHost', this.state.usbHost, this.driver.usbHost, (_r = config.usbHosts) !== null && _r !== void 0 ? _r : {}, this.controlInfo.usbHost, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.usbHost = this.driver.usbHost;
                }
            }
            this.bridges.validated = new ControllessBridge('validated', this.state.validated, this.valueCallback, this.log);
            if (this.driver.videoInput) {
                this.controlInfo.videoInput = yield this.computeControlInfo('videoInput', config);
                if (this.controlInfo.videoInput.hasControl) {
                    this.bridges.videoInput = new VideoInputBridge('videoInput', this.state.videoInput, this.driver.videoInput, videoInputs, this.controlInfo.videoInput, this.state.power, this.transport, this.valueCallback, this.framework, this.log);
                    this.controls.videoInput = this.driver.videoInput;
                }
            }
            if (this.driver.videoInputs) {
                // We already got this.controlInfo.videoInputs at the beginning of this function.
                if (this.controlInfo.videoInputs.hasControl) {
                    this.bridges.videoInputs = new VideoInputsBridge(this.driver.videoInputs, this.state.videoInputs, this.transport, this.framework, this.log);
                    this.controls.videoInputs = this.driver.videoInputs;
                }
            }
            if (this.driver.videoMatrixRoutes) {
                this.controlInfo.videoMatrixRoutes = yield this.computeControlInfo('videoMatrixRoutes', config);
                if (this.controlInfo.videoMatrixRoutes.hasControl) {
                    this.bridges.videoMatrixRoutes = new VideoMatrixRoutesBridge(config, this.state.videoMatrixRoutes, this.driver.videoMatrixRoutes, this.state.detected, this.controlInfo.videoMatrixRoutes, this.state.power, this.transport, this.valueCallback, this.log);
                    this.controls.videoMatrixRoutes = this.driver.videoMatrixRoutes;
                }
            }
            this.connectedSubscription = this.connected.subscribe(this.onAfterConnectedChanged);
            this.powerStateSubscription = this.state.power.subscribe(this.onAfterPowerChanged);
            this.initializeHandlers();
        });
    }
    destroy() {
        this.reset();
    }
    getControlInfo(controlName) {
        return this.controlInfo[controlName];
    }
    getControls() {
        return this.controls;
    }
    getBridges() {
        return this.bridges;
    }
    getPreControlValues() {
        return __awaiter(this, void 0, void 0, function* () {
            // detection
            if (this.bridges.detected) {
                yield this.bridges.detected.getInitialValues();
            }
            // validation
            const results = {};
            for (const bridge of Object.values(this.bridges)) {
                if (bridge.validate) {
                    const result = yield bridge.validate();
                    if (result)
                        Sutl.merge(results, result);
                }
            }
            Sutl.pruneEmptyObjects(results);
            // Force notification when results are empty since the validated state gets initialized to
            // an empty object. This prevents the following problem (SNTY=2004):
            // 1. new dsp configuration -> validated results
            // 2. new empty dsp configuration -> no validated results get sent, old results still seen
            const resultsAreEmpty = Object.keys(results).length === 0;
            this.state.validated.update(results, resultsAreEmpty);
            if (this.bridges.volumeMute && this.bridges.volumeMute.isActive) {
                this._hasVolumeMute = true;
            }
        });
    }
    getInitialValues() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const alreadyQueried = ['detected', 'power', 'validated'];
            const firstQueries = ['cameras', 'videoInputs'].filter(name => name in this.bridges);
            const secondQueries = Object.keys(this.bridges)
                .filter(name => !alreadyQueried.includes(name) && !firstQueries.includes(name))
                .sort();
            const queries = [...firstQueries, ...secondQueries];
            let powerOnQueriesDone = true;
            for (const name of queries) {
                const control = this.controls[name];
                if (control && !((_b = (_a = this.controlInfo[name]) === null || _a === void 0 ? void 0 : _a.canGet(this.state.power.value)) !== null && _b !== void 0 ? _b : false)) {
                    powerOnQueriesDone = false;
                    continue;
                }
                yield this.bridges[name].getInitialValues();
            }
            this.powerOnQueriesDone = powerOnQueriesDone;
        });
    }
    initializeHandlers() {
        for (const bridge of Object.values(this.bridges)) {
            for (const key of Object.keys(bridge.queryHandlers)) {
                if (key in this.queryHandlers) {
                    this.log.error(`multiple query handlers for ${key}`);
                }
                else {
                    assertIsQueryParam(key);
                    const qh = bridge.queryHandlers[key];
                    if (qh !== undefined)
                        this.queryHandlers[key] = qh;
                }
            }
            for (const key of Object.keys(bridge.setFilters)) {
                if (key in this.setFilters) {
                    this.log.error(`multiple set filters for ${key}`);
                }
                else {
                    assertIsSetParam(key);
                    const sf = bridge.setFilters[key];
                    if (sf !== undefined)
                        this.setFilters[key] = sf;
                }
            }
            for (const key of Object.keys(bridge.setHandlers)) {
                if (key in this.setHandlers) {
                    this.log.error(`multiple set handlers for ${key}`);
                }
                else {
                    assertIsSetParam(key);
                    const sh = bridge.setHandlers[key];
                    if (sh !== undefined)
                        this.setHandlers[key] = sh;
                }
            }
        }
    }
    isStateless(controlName) {
        const controlInfo = this.controlInfo[controlName];
        if (controlInfo === undefined)
            throw new Error(`controlInfo undefined for ${controlName}`);
        return controlInfo.isStateless;
    }
    isToggle(controlName) {
        const controlInfo = this.controlInfo[controlName];
        if (controlInfo === undefined)
            throw new Error(`controlInfo undefined for ${controlName}`);
        return controlInfo.isToggle;
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            // Handle any deferred queries or deferred set operations (i.e., we were configuring when
            // they were received).
            for (const param of this.deferredQueries) {
                this.query(param);
            }
            this.deferredQueries.length = 0;
            this.doNextSetOperation();
        });
    }
    poll(name) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const bridge of Object.values(this.bridges)) {
                if ((bridge === null || bridge === void 0 ? void 0 : bridge.pollName) === name) {
                    yield bridge.poll();
                }
            }
        });
    }
    query(param) {
        if (!this.connected.value && !this._canQueryWhenDisconnected) {
            this.log.warning(`can't query ${param} while disconnected`);
            return;
        }
        if (this.framework.phase === 'configuring') {
            // If we're configuring, save queries until we're done.
            if (!this.deferredQueries.includes(param))
                this.deferredQueries.push(param);
            return;
        }
        const queryHandler = this.queryHandlers[param];
        if (queryHandler === undefined) {
            this.log.warning(`no query handler defined for param: ${param}`);
            return;
        }
        queryHandler(param);
    }
    set(param, value) {
        return __awaiter(this, void 0, void 0, function* () {
            // Add the set operation to the queue, then process the queue.
            return new Promise((resolve, reject) => {
                // Remove similar set operations in queue if necessary.
                const setFilter = this.setFilters[param];
                if (setFilter) {
                    Sutl.filterInPlace(this.setOperations, (op) => setFilter(param, value, op.param, op.value));
                }
                // Add set operation to queue and execute if possible.
                this.setOperations.push({ param, value, resolve, reject });
                if (this.framework.phase === 'controlling')
                    this.doNextSetOperation();
            });
        });
    }
    doNextSetOperation() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.currentSetOp)
                return; // already processing a set operation
            this.currentSetOp = this.setOperations.shift();
            if (!this.currentSetOp)
                return; // no more set operations to process
            if (!this.connected.value &&
                (this.currentSetOp.param !== 'power' || this.currentSetOp.value !== true)) {
                this.log.info(`ignoring set request for ${this.currentSetOp.param} while disconnected`);
            }
            else {
                const setHandler = this.setHandlers[this.currentSetOp.param];
                if (setHandler === undefined) {
                    this.log.warning(`no set handler for param: ${this.currentSetOp.param}`);
                }
                else {
                    try {
                        yield setHandler(this.currentSetOp.param, this.currentSetOp.value);
                    }
                    catch (error) {
                        this.log.error(`set: ${error}`);
                    }
                }
            }
            this.currentSetOp = undefined;
            if (this.framework.phase === 'controlling')
                this.doNextSetOperation();
        });
    }
    doPowerOnQueriesIfNecessary() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.powerOnQueriesDone && this.state.power.value && this.connected.value) {
                const promises = [];
                if (this.reconfigureWhenPoweredOn) {
                    if (this.framework.phase === 'controlling') {
                        this.framework.changePhase('configuring');
                        return;
                    }
                }
                this.powerOnQueriesDone = true;
                for (const name of Object.keys(this.controls)) {
                    if (((_b = (_a = this.controlInfo[name]) === null || _a === void 0 ? void 0 : _a.powerOffRestrictions) !== null && _b !== void 0 ? _b : []).includes('get')) {
                        const bridge = this.bridges[name];
                        if (bridge) {
                            promises.push(bridge.getInitialValues());
                        }
                        else {
                            this.log.error(`no ${name} bridge defined`);
                        }
                    }
                }
                yield Promise.all(promises);
            }
        });
    }
    onAfterConnectedChanged(prevValue) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.doPowerOnQueriesIfNecessary();
        });
    }
    onAfterPowerChanged(prevValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state.power.value)
                yield this.doPowerOnQueriesIfNecessary();
        });
    }
    reset() {
        if (this.connectedSubscription !== undefined) {
            this.connected.unsubscribe(this.connectedSubscription);
            this.connectedSubscription = undefined;
        }
        if (this.powerStateSubscription !== undefined) {
            this.state.power.unsubscribe(this.powerStateSubscription);
            this.powerStateSubscription = undefined;
        }
        Object.keys(this.bridges).forEach(k => {
            this.bridges[k].destroy();
            delete this.bridges[k];
        });
        Object.keys(this.controls).forEach(k => delete this.controls[k]);
        Object.keys(this.queryHandlers).forEach(k => delete this.queryHandlers[k]);
        Object.keys(this.setFilters).forEach(k => delete this.setFilters[k]);
        Object.keys(this.setHandlers).forEach(k => delete this.setHandlers[k]);
        this.state.reset(['storage']);
        const numSubscriptions = this.state.getNumSubscriptions(true);
        if (numSubscriptions > 0) {
            this.log.error(`${numSubscriptions} state subscriptions, unsubscribing`);
            this.state.unsubscribeAll();
        }
        this.controlInfo = {};
        this.offlineControlInfo = {};
    }
}
